import React, { useRef, useCallback, useContext, useEffect, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import ZoomControl from 'mapbox-gl-controls/lib/zoom';
import CompassControl from 'mapbox-gl-controls/lib/compass';
import RulerControl from 'mapbox-gl-controls/lib/ruler';
import TooltipControl from 'mapbox-gl-controls/lib/tooltip';
import { polygon } from '@turf/helpers';
import centroid from '@turf/centroid';
import destination from '@turf/destination';
import bbox from '@turf/bbox';
import languageControl from '../../Map/controls/LanguageControl';
import { attachMapAnalytics, sendEvent } from '../../helpers/analytics';
import {
  getColor,
  LAYERS,
  buildingIdMapFilter,
  LAYERS_TO_COLORIZE,
  STATUS,
  initMapLayers,
} from '../../Map/layers';
import {
  FlatsFiltersContentContext,
  FlatsFiltersEmptyStatesContext,
} from '../../FlatsFilters/FlatsFiltersContext';
import { EMPTY_STATES } from '../FlatsEmptyStates/FlatsEmptyStates';
import { useHouse3DTooltips } from './useHouse3DTooltips';
import { initFPSMeasurer } from '../../helpers/fps-measurer';
import { getBuildingsDataByIds } from '../../helpers/getBuildingsDataByIds';

const BBOX_BEARINGS = [45, -45, 135, -135];
const BBOX_OPTIONS = { units: 'meters' };

const getBoundingBox = (sections) => {
  const destinations = [];

  sections.forEach((id) => {
    const { geometry, altitude } = window.params.sectionContours[id];
    const sectionPolygon = polygon(geometry.coordinates);
    const sectionCentroid = centroid(sectionPolygon);
    const distance = altitude * Math.sqrt(2);

    BBOX_BEARINGS.forEach((bearing) => {
      destinations.push(destination(sectionCentroid, distance, bearing, BBOX_OPTIONS));
    });
  });

  return bbox({
    type: 'FeatureCollection',
    features: destinations,
  });
};

const MAX_MENU_WIDTH = 819;
const TOP_PADDING = Math.round(document.querySelector('#flats-prototype').clientHeight / 3);
const MAP_PADDING = window.isMobile
  ? 20
  : {
      top: TOP_PADDING,
      bottom: 0,
      left: Math.min(MAX_MENU_WIDTH, Math.round(window.innerWidth * 0.6)),
      right: 30,
    };
const FIT_BOUNDS_OPTIONS = {
  duration: 1000,
  padding: MAP_PADDING,
  animate: true,
  easing(t) {
    return 1 - (1 - t) ** 5;
  },
};

const FlatsMap = ({ buildingId }) => {
  const intl = useIntl();
  const { searchParams, updateSearchParams, isMapVisible, setIsMapVisible, isUpdating } =
    useContext(FlatsFiltersContentContext);

  const { setEmptyState } = useContext(FlatsFiltersEmptyStatesContext);
  const [isHouseClicked, setIsHouseClicked] = useState(false);
  const [houseProperties, setHouseProperties] = useState(null);
  const [buildingData, setBuildingData] = useState(null);
  const mapContainerRef = useRef(null);
  const map = useRef(null);
  const buildingBoundingBox = useMemo(
    () => getBoundingBox(Object.keys(window.params.sectionContours)),
    [],
  );

  useEffect(() => {
    getBuildingsDataByIds([buildingId], (data) => {
      setBuildingData(data);
    });
  }, [buildingId]);

  const handleShowLayoutsForSelectedHouse = useCallback((houseId) => {
    setIsMapVisible(false);

    updateSearchParams((currentSearchParams) => ({
      ...currentSearchParams,
      house_id: [houseId],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useHouse3DTooltips(map.current, searchParams.house_id, handleShowLayoutsForSelectedHouse);

  const filterByBuilding = () => {
    map.current.setFilter(LAYERS.points, ['==', buildingIdMapFilter, buildingId]);
    initMapLayers(map.current);
    /* todo: set visibility: none ? */
    map.current.setLayoutProperty('buildings_osm_3d', 'visibility', 'none');
    map.current.setLayoutProperty('poi_custom', 'visibility', 'none');
    map.current.setLayoutProperty('poi_z14', 'visibility', 'none');
    map.current.setLayoutProperty('poi_z15', 'visibility', 'none');
    map.current.setLayoutProperty('poi_z16', 'visibility', 'none');
  };

  const getSectionIds = useCallback(() => searchParams.section_id, [searchParams]);

  const colorize = useCallback(() => {
    LAYERS_TO_COLORIZE.forEach((layerId) => {
      const layer = map.current.getLayer(layerId);

      if (!layer) return;
      const colorProperty = `${layer.type}-color`;
      const colorValue = getColor(buildingId, {
        selectedSectionIds: getSectionIds(),
        layerId: layer.id,
        filtered: buildingData,
      });
      map.current.setPaintProperty(layer.id, colorProperty, colorValue);
    });
  }, [getSectionIds, buildingData, buildingId]);

  const deselectHouses = useCallback(
    (event) => {
      if (event.defaultPrevented) {
        return;
      }
      map.current.fitBounds(buildingBoundingBox, FIT_BOUNDS_OPTIONS);
      updateSearchParams({
        ...searchParams,
        house_id: [],
        section_id: [],
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams, updateSearchParams],
  );

  const handleSectionClick = useCallback(
    (event) => {
      event.preventDefault();
      const [firstFeature] = event.features;
      const zoomLevel = map.current.getZoom();
      const houseId = firstFeature.properties.lun_house_id;
      const houseSections = window.params.houses[houseId] ? window.params.houses[houseId] : [];

      if (searchParams.house_id.includes(houseId)) {
        updateSearchParams({
          ...searchParams,
          house_id: searchParams.house_id.filter((v) => v !== houseId),
          section_id: searchParams.section_id.filter(
            (v) => !houseSections.includes(v),
          ),
        });
      } else {
        updateSearchParams({
          ...searchParams,
          house_id: [...searchParams.house_id, houseId],
          section_id: [...searchParams.section_id, ...houseSections],
        });
        setIsHouseClicked(true);
      }
      setHouseProperties(firstFeature.properties);
      sendEvent(window.eventCategory, `map_zoom_${zoomLevel}`, 'section_click');
    },
    [searchParams, updateSearchParams],
  );

  const handleFitBounds = useCallback(() => {
    const sectionIds = getSectionIds();
    const bounds = getBoundingBox(
      Object.keys(window.params.sectionContours).filter((key) => sectionIds.includes(Number(key))),
    );

    if (sectionIds.length > 0) {
      map.current.fitBounds(bounds, FIT_BOUNDS_OPTIONS);
    } else {
      map.current.fitBounds(buildingBoundingBox, FIT_BOUNDS_OPTIONS);
    }
  }, [getSectionIds, buildingBoundingBox]);

  const addControls = () => {
    map.current.addControl(languageControl);
    if (window.isMobile) {
      map.current.addControl(new CompassControl(), 'bottom-right');
      map.current.addControl(new ZoomControl(), 'bottom-right');
    } else {
      map.current.addControl(new mapboxgl.AttributionControl({ compact: true }), 'top-right');
      map.current.addControl(new ZoomControl(), 'bottom-right');
      if (!window.isTouch) map.current.addControl(new RulerControl(), 'bottom-right');
      map.current.addControl(new CompassControl(), 'bottom-right');
    }
  };

  const initMap = useCallback(() => {
    const container = mapContainerRef.current;

    if (!container || map.current) return;

    map.current = new mapboxgl.Map({
      container,
      style: window.mapStreetsStyle,
      zoom: 15,
      center: window.params.center,
      bounds: buildingBoundingBox,
      fitBoundsOptions: {
        maxZoom: 18,
        padding: MAP_PADDING,
      },
      minZoom: 10,
      maxZoom: 18,
      pitch: 60,
      scrollZoom: false,
      attributionControl: false,
      maxBounds: window.mapMaxBounds,
    });

    addControls();

    map.current.on('load', () => {
      if (!window.isTouch) {
        const tooltipControl = new TooltipControl({
          layer: LAYERS.sections,
          getContent: (event) => {
            const { lun_section_id: lunSectionId, lun_building_id: lunBuildingId } =
              event.features[0].properties;
            const section = buildingData[0].sections.find((s) => s.section_id === lunSectionId);

            const notStarted = section?.status === STATUS.FUTURE_AVAILABLE;
            const sold = !section && lunBuildingId === buildingId;

            if (sold) {
              return intl.formatMessage({ id: EMPTY_STATES.ALL_SOLD });
            }

            if (notStarted) {
              return intl.formatMessage({ id: EMPTY_STATES.NOT_STARTED });
            }

            return null;
          },
        });

        map.current.addControl(tooltipControl);
      }

      initFPSMeasurer(map.current);
    });

    map.current.on('style.load', () => {
      // change language if map style switched
      languageControl.setLanguage(window.currentLanguage);
      filterByBuilding();
      colorize();
    });

    attachMapAnalytics(container);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingData]);

  useEffect(() => {
    if (!buildingData) return;

    initMap();
  }, [buildingData, initMap]);

  useEffect(() => {
    if (!map.current) return;
    map.current.on('click', LAYERS.sections, handleSectionClick);
    map.current.on('click', deselectHouses);

    // eslint-disable-next-line consistent-return
    return () => {
      map.current.off('click', LAYERS.sections, handleSectionClick);
      map.current.off('click', deselectHouses);
    };
  }, [handleSectionClick, deselectHouses]);

  useEffect(() => {
    if (!map.current) return;
    colorize();
  }, [colorize]);

  useEffect(() => {
    if (isHouseClicked && !isUpdating) {
      setIsHouseClicked(false);
      if (
        houseProperties.developer_offer === EMPTY_STATES.NOT_STARTED ||
        houseProperties.developer_offer === EMPTY_STATES.ALL_SOLD
      ) {
        setEmptyState(houseProperties.developer_offer);
      } else {
        setEmptyState(EMPTY_STATES.EMPTY);
      }
    }
  }, [isUpdating, isHouseClicked, setIsMapVisible, houseProperties, setEmptyState]);

  useEffect(() => {
    if (!map.current) return;
    handleFitBounds();
  }, [handleFitBounds]);

  useEffect(() => {
    if (!map.current) return;
    if (isMapVisible) {
      map.current.resize();
    }
  }, [isMapVisible]);

  return <div className="FlatsMap" ref={mapContainerRef} />;
};

FlatsMap.propTypes = {
  buildingId: PropTypes.number.isRequired,
};

export default FlatsMap;
